// SettingsPage.tsx — Read-only admin allowlist with deep-link
// instructions for adding / removing via Firebase Console.

import { useMemo } from 'react';
import { useFirestoreQuery } from '@/hooks/useFirestoreQuery';
import { listAdmins } from '@/services/firestore/admins';
import { useAuth } from '@/services/useAuth';
import { SkeletonList } from '@/components/SkeletonRow';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { RolePill } from '@/components/pills';
import { fmtDate } from '@/utils/format';
import type { AdminAllowlistEntry } from '@/types/domain';

const FIRESTORE_ADMINS_URL =
  'https://console.firebase.google.com/project/social-mesh-app/firestore/data/~2Fadmins';
const FIREBASE_AUTH_USERS_URL =
  'https://console.firebase.google.com/project/social-mesh-app/authentication/users';

export function SettingsPage() {
  const { user } = useAuth();
  const { data, loading, error } = useFirestoreQuery('admins:list', listAdmins);

  const admins = useMemo(() => {
    if (!data) return [] as AdminAllowlistEntry[];
    return [...data].sort((a, b) => {
      if (a.role !== b.role) {
        if (a.role === 'owner') return -1;
        if (b.role === 'owner') return 1;
      }
      return a.uid.localeCompare(b.uid);
    });
  }, [data]);

  if (loading && !data) return <SkeletonList count={3} />;
  if (error) return <ErrorState message={error.message} />;

  return (
    <>
      <header className="console-page-header">
        <div>
          <h1 className="console-page-title">Settings</h1>
          <div className="console-page-subtitle">
            Console-wide configuration · {admins.length} admin{admins.length === 1 ? '' : 's'}
          </div>
        </div>
      </header>

      <div className="console-card">
        <div className="console-card-title">Admin allowlist</div>
        {admins.length === 0 ? (
          <EmptyState
            icon="shield"
            title="No admins on the list"
            body="The bootstrap allowlist in services/auth.tsx is the only path until at least one admin doc is added."
          />
        ) : (
          <AdminsTable admins={admins} currentUid={user?.uid ?? null} />
        )}
      </div>

      <ManageCard />
    </>
  );
}

function AdminsTable({
  admins,
  currentUid,
}: {
  admins: AdminAllowlistEntry[];
  currentUid: string | null;
}) {
  return (
    <div className="console-table-wrap">
      <table className="console-table">
        <thead>
          <tr>
            <th>Uid / Email</th>
            <th>Role</th>
            <th>Added</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {admins.map((a) => {
            const isYou = currentUid === a.uid;
            return (
              <tr key={a.uid}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: 'var(--text-primary)' }}>
                      {a.uid}
                    </code>
                    {isYou && (
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--accent-magenta)', background: 'rgba(233,30,140,0.12)', padding: '2px 8px', borderRadius: 999 }}>
                        You
                      </span>
                    )}
                  </div>
                  {a.email && (
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
                      {a.email}
                    </div>
                  )}
                </td>
                <td><RolePill role={a.role} /></td>
                <td>{fmtDate(a.createdAt)}</td>
                <td>{a.note ?? <span style={{ color: 'var(--text-muted)' }}>-</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ManageCard() {
  return (
    <div className="console-card">
      <div className="console-card-title">Add or remove admins</div>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 14 }}>
        Writes to the <code>admins/</code> collection are blocked by Firestore rules (<code>allow write: if false</code>) on purpose: an admin allowlist is too load-bearing to manage from a client UI without a separate confirmation + audit story. Use the Firebase Console:
      </p>
      <ol style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.8, margin: '0 0 16px 18px' }}>
        <li>
          Open the <a href={FIRESTORE_ADMINS_URL} target="_blank" rel="noopener" style={{ color: 'var(--accent-magenta)', textDecoration: 'none', borderBottom: '1px dashed currentColor' }}>admins/ collection in Firebase Console</a>.
        </li>
        <li>
          Click <strong>Add document</strong>. Set the document id to the teammate's Firebase Auth uid (find it in <a href={FIREBASE_AUTH_USERS_URL} target="_blank" rel="noopener" style={{ color: 'var(--accent-magenta)', textDecoration: 'none', borderBottom: '1px dashed currentColor' }}>Authentication → Users</a>).
        </li>
        <li>
          Add fields: <code>role: "admin"</code> (or <code>"owner"</code>), <code>email: "&lt;their email&gt;"</code>, <code>note: "&lt;why&gt;"</code>, <code>createdAt: serverTimestamp()</code>.
        </li>
        <li>Save. Their next sign-in lands them in the console.</li>
      </ol>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55, margin: 0 }}>
        <strong>To remove:</strong> delete the doc in the same collection. The teammate's next page load sees the access-denied splash; existing sessions stay until they refresh.
      </p>
    </div>
  );
}
